/**
 * CPF e CNPJ.
 *
 * ⚠️ Atenção ao CNPJ alfanumérico: desde 31/07/2026 os novos registros podem
 * conter letras nas 12 primeiras posições. Os dois dígitos verificadores
 * continuam numéricos, e os CNPJs já existentes seguem válidos sem alteração.
 *
 * O algoritmo é o mesmo módulo 11 de sempre — o que muda é que cada caractere
 * entra no cálculo pelo seu código ASCII menos 48. Assim '0' vale 0, '9' vale
 * 9, 'A' vale 17 e 'Z' vale 35, e a conta antiga continua funcionando para os
 * documentos antigos.
 *
 * Base: Instrução Normativa RFB nº 2.229/2024. ⚠️ A implementação segue a
 * descrição pública do cálculo; vale conferir contra um CNPJ com letras real
 * quando você tiver um em mãos.
 */

export type TipoDocumento = 'CPF' | 'CNPJ';

const valorCaractere = (c: string): number => c.charCodeAt(0) - 48;

/** Módulo 11 sobre uma sequência de pesos. Resto menor que 2 vira dígito 0. */
function modulo11(valores: readonly number[], pesos: readonly number[]): number {
  let soma = 0;
  for (let i = 0; i < valores.length; i++) soma += valores[i]! * pesos[i]!;
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

const PESOS_CNPJ_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CPF_1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CPF_2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];

/** Remove tudo que não seja dígito ou letra maiúscula. */
export function limparDocumento(bruto: string): string {
  return bruto.toUpperCase().replace(/[^0-9A-Z]/g, '');
}

export function validarCpf(bruto: string): boolean {
  const v = bruto.replace(/\D/g, '');
  if (v.length !== 11) return false;
  // Sequências repetidas passam no módulo 11 mas não são CPF de ninguém.
  if (/^(\d)\1{10}$/.test(v)) return false;

  const d = [...v].map(Number);
  return (
    modulo11(d.slice(0, 9), PESOS_CPF_1) === d[9] && modulo11(d.slice(0, 10), PESOS_CPF_2) === d[10]
  );
}

export function validarCnpj(bruto: string): boolean {
  const v = limparDocumento(bruto);
  if (v.length !== 14) return false;
  // Os dois últimos são sempre numéricos, mesmo no formato alfanumérico.
  if (!/^[0-9A-Z]{12}\d{2}$/.test(v)) return false;
  if (/^(.)\1{13}$/.test(v)) return false;

  const valores = [...v].map(valorCaractere);
  return (
    modulo11(valores.slice(0, 12), PESOS_CNPJ_1) === Number(v[12]) &&
    modulo11(valores.slice(0, 13), PESOS_CNPJ_2) === Number(v[13])
  );
}

export function ehAlfanumerico(bruto: string): boolean {
  return /[A-Z]/.test(limparDocumento(bruto));
}

export interface DocumentoDetectado {
  tipo: TipoDocumento;
  valor: string;
  valido: boolean;
  motivo?: string;
}

/** Detecta pelo comprimento: 11 posições é CPF, 14 é CNPJ. */
export function detectarDocumento(bruto: string): DocumentoDetectado | null {
  const v = limparDocumento(bruto);
  if (v.length === 0) return null;

  if (v.length === 11 && /^\d+$/.test(v)) {
    return validarCpf(v)
      ? { tipo: 'CPF', valor: v, valido: true }
      : { tipo: 'CPF', valor: v, valido: false, motivo: 'CPF inválido: o dígito verificador não confere' };
  }

  if (v.length === 14) {
    return validarCnpj(v)
      ? { tipo: 'CNPJ', valor: v, valido: true }
      : { tipo: 'CNPJ', valor: v, valido: false, motivo: 'CNPJ inválido: o dígito verificador não confere' };
  }

  return {
    tipo: v.length > 11 ? 'CNPJ' : 'CPF',
    valor: v,
    valido: false,
    motivo: `CPF tem 11 posições e CNPJ tem 14; este tem ${v.length}`,
  };
}

export function formatarDocumento(bruto: string): string {
  const v = limparDocumento(bruto);
  if (v.length === 11) return v.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  if (v.length === 14)
    return v.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5');
  return v;
}
